/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Pre-launch (early access) mobile bottom navigation with overflow menu support.
 */

import React, { useState, useRef, useEffect, useMemo } from'react';
import { Box, HamburgerMenuIcon, Text, UnstyledButton } from'@/shared/ui';
import { useTranslation } from'react-i18next';
import { useLocation, useNavigate } from'react-router';
import { useEarlyAccessStatus } from'@/shared/hooks/use-early-access-status';
import { cn } from'@/shared/ui/utils/tailwind';
import { MAX_VISIBLE, type NavItem, type MobileNavProps } from'./MobileNav.types';
import { buildPreLaunchNavItems } from'./MobileNav.items';
import { NavButton, OverflowMenu } from'./MobileNav.parts';

export const PreLaunchMobileNav: React.FC<MobileNavProps> = ({ onScrollTo, activeSection }) => {
  const { t } = useTranslation('@pages/landing');
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const earlyAccessCompleted = useEarlyAccessStatus();

  const allNavItems = buildPreLaunchNavItems({ t, navigate, onScrollTo, earlyAccessCompleted });
  const needsOverflow = allNavItems.length > MAX_VISIBLE;

  const visibleItems = useMemo(
    () => (needsOverflow ? allNavItems.slice(0, MAX_VISIBLE - 1) : allNavItems),
    [allNavItems, needsOverflow],
  );
  const overflowItems = useMemo(
    () => (needsOverflow ? allNavItems.slice(MAX_VISIBLE - 1) : []),
    [allNavItems, needsOverflow],
  );
  const isMoreActive = useMemo(
    () => overflowItems.some((item) => activeSection === item.id),
    [overflowItems, activeSection],
  );

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const resolveActive = (item: NavItem): boolean => {
    const isAccessActive =
      item.id ==='access' &&
      (activeSection ==='early-access' || location.pathname.startsWith('/early-access'));
    return isAccessActive || activeSection === item.id;
  };

  return (
    <Box
      className={cn(
        'fixed bottom-0 left-0 right-0 z-1000',
        'h-(--bottom-nav-height,64px)',
        'bg-(--surface-primary)',
        'border-t border-(--border-subtle)',
        'pb-[env(safe-area-inset-bottom)]',
        'shadow-[var(--shadow-sm)]',
        'md:hidden',
      )}
    >
      <Box className="flex h-full items-stretch max-w-[600px] mx-auto">
        {visibleItems.map((item) => (
          <NavButton key={item.id} item={item} isActive={resolveActive(item)} />
        ))}

        {needsOverflow && (
          <div className="relative flex flex-1" ref={menuRef}>
            <UnstyledButton
              onClick={() => setMenuOpen((o) => !o)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-(--space-1) relative',
                'text-(--text-tertiary) transition-colors duration-200',
                isMoreActive &&'text-(--seed-base)',
                isMoreActive &&'active',
              )}
              aria-label={t('navigation.menu')}
            >
              {isMoreActive && (
 <Text component="span" className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-(--seed-base)"/>
              )}
              <HamburgerMenuIcon className="w-5 h-5" />
              <Text component="span" className="text-meta tracking-(--tracking-wide) font-(--font-light) whitespace-nowrap leading-none">
                {t('navigation.menu')}
              </Text>
            </UnstyledButton>

            {menuOpen && (
              <OverflowMenu
                items={overflowItems}
                activeSection={activeSection}
                menuTitle={t('navigation.menu')}
                onSelect={(item) => {
                  item.action();
                  setMenuOpen(false);
                }}
              />
            )}
          </div>
        )}
      </Box>
    </Box>
  );
};
